import { formatDate } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  PlusCircle,
  Users,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Repeat,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL } from "../../api/header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PageContainer } from "@/components/layout/PageContainer";

const Tasks = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [isDeleteAssignmentDialogOpen, setIsDeleteAssignmentDialogOpen] =
    useState(false);
  const [deleteAssignmentId, setDeleteAssignmentId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const [taskData, setTaskData] = useState({
    id: null,
    title: "",
    description: "",
    priority: "medium",
    is_recurring: false,
    recurrence_type: null,
    recurrence_value: null,
  });

  const [assignmentData, setAssignmentData] = useState({
    start_date: "",
    end_date: "",
    employee_id: "",
    notifier_id: "",
  });

  const permissions = useSelector((state) => state.userpermission_data);

  const hasPermission = React.useCallback(
    (permission) => {
      const perms = permissions || [];
      return perms && perms.includes(permission);
    },
    [permissions],
  );
  // Queries
  const { data: tasksData, isLoading } = useQuery({
    queryKey: ["tasks", currentPage, pageSize, searchText],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/tasks`, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText,
        },
      });
      return res.data;
    },
    enabled: hasPermission("task read"),
  });

  const tasks = tasksData?.data || [];
  const paginationMeta = {
    current_page: tasksData?.meta?.current_page || tasksData?.current_page || 1,
    last_page: tasksData?.meta?.last_page || tasksData?.last_page || 1,
    per_page: tasksData?.meta?.per_page || tasksData?.per_page || 15,
    total: tasksData?.meta?.total || tasksData?.total || tasks.length,
  };

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/employee`);
      return res.data.data || res.data;
    },
    enabled: hasPermission("employee read"),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/user`);
      return res.data.data || res.data;
    },
    enabled: hasPermission("user read"),
  });

  const { data: assignments = [], isLoading: assignmentsLoading } = useQuery({
    queryKey: ["task-assignments"],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/task-assignments`);
      return res.data.data || res.data;
    },
    enabled: showAssignmentsModal && hasPermission("task assignment read"),
  });

  // Mutations
  const deleteTaskMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`${API_BASE_URL}/tasks/${id}`);
    },
    onSuccess: () => {
      toast.success("Task deleted successfully");
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["task-assignments"]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete task");
    },
  });

  const saveTaskMutation = useMutation({
    mutationFn: async ({ taskData, assignmentData }) => {
      const url = taskData.id
        ? `${API_BASE_URL}/tasks/${taskData.id}`
        : `${API_BASE_URL}/tasks`;
      const method = taskData.id ? "put" : "post";

      const taskPayload = {
        ...taskData,
        recurrence_type: taskData.is_recurring
          ? taskData.recurrence_type
          : null,
        recurrence_value: taskData.is_recurring
          ? taskData.recurrence_value
          : null,
      };

      const taskRes = await apiClient[method](url, taskPayload);
      const savedTask = taskRes.data.data || taskRes.data;

      // Create assignment if provided
      if (
        assignmentData.start_date &&
        assignmentData.employee_id &&
        assignmentData.notifier_id
      ) {
        const assignmentPayload = {
          task_id: savedTask.id,
          employee_id: assignmentData.employee_id,
          notifier_id: assignmentData.notifier_id,
          start_date: assignmentData.start_date,
          end_date: assignmentData.end_date || null,
        };

        await apiClient.post(
          `${API_BASE_URL}/task-assignments`,
          assignmentPayload,
        );
      }

      return savedTask;
    },
    onSuccess: () => {
      toast.success(
        `Task ${isEdit ? "updated" : "created"} successfully${assignmentData.employee_id ? " with assignment" : ""}`,
      );
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["task-assignments"]);
      closeAddModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to save task");
    },
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: async (assignmentId) => {
      await apiClient.delete(`${API_BASE_URL}/task-assignments/${assignmentId}`);
    },
    onSuccess: () => {
      toast.success("Task assignment deleted successfully");
      queryClient.invalidateQueries(["task-assignments"]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete assignment",
      );
    },
  });

  const handleDelete = React.useCallback((id) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  }, []);

  const confirmDelete = () => {
    deleteTaskMutation.mutate(deleteId);
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteAssignment = (assignmentId) => {
    setDeleteAssignmentId(assignmentId);
    setIsDeleteAssignmentDialogOpen(true);
  };

  const confirmDeleteAssignment = () => {
    deleteAssignmentMutation.mutate(deleteAssignmentId);
    setIsDeleteAssignmentDialogOpen(false);
  };

  const openAddModal = React.useCallback((task = null) => {
    if (task) {
      setTaskData({
        ...task,
        is_recurring: task.is_recurring || false,
        recurrence_type: task.recurrence_type || null,
        recurrence_interval: task.recurrence_interval || null,
        recurrence_end_date: task.recurrence_end_date || "",
        checklists: task.checklists || [],
      });
      setIsEdit(true);
    } else {
      setTaskData({
        title: "",
        description: "",
        assigned_to: "",
        status: "pending",
        priority: "medium",
        start_date: "",
        due_date: "",
        is_recurring: false,
        recurrence_type: null,
        recurrence_interval: null,
        recurrence_end_date: "",
        checklists: [],
      });
      setIsEdit(false);
    }
    setAssignmentData({
      start_date: "",
      end_date: "",
      employee_id: "",
      notifier_id: "",
    });
    setShowAddModal(true);
  }, []);

  const closeAddModal = () => {
    setShowAddModal(false);
    setTaskData({
      id: null,
      title: "",
      description: "",
      priority: "medium",
      is_recurring: false,
      recurrence_type: null,
      recurrence_value: null,
    });
    setAssignmentData({
      start_date: "",
      end_date: "",
      employee_id: "",
      notifier_id: "",
    });
    setIsEdit(false);
  };

  const handleView = (task) => {
    setSelectedTask(task);
    setShowViewModal(true);
  };

  const handleSave = () => {
    if (!taskData.title?.trim()) {
      toast.error("Task title is required");
      return;
    }

    if (taskData.is_recurring && !taskData.recurrence_type) {
      toast.error("Recurrence type is required for recurring tasks");
      return;
    }

    if (
      taskData.is_recurring &&
      (!taskData.recurrence_value || taskData.recurrence_value < 1)
    ) {
      toast.error("Recurrence value must be at least 1");
      return;
    }

    // Validate assignment if any field is provided
    if (
      assignmentData.start_date ||
      assignmentData.employee_id ||
      assignmentData.notifier_id
    ) {
      if (!assignmentData.start_date) {
        toast.error("Start date is required when creating assignment");
        return;
      }
      if (!assignmentData.employee_id) {
        toast.error("Employee is required when creating assignment");
        return;
      }
      if (!assignmentData.notifier_id) {
        toast.error("Notifier is required when creating assignment");
        return;
      }
      if (
        assignmentData.end_date &&
        new Date(assignmentData.end_date) < new Date(assignmentData.start_date)
      ) {
        toast.error("End date must be after start date");
        return;
      }
    }

    saveTaskMutation.mutate({ taskData, assignmentData });
  };

  const getPriorityBadgeVariant = (priority) => {
    const priorityMap = {
      low: "success",
      medium: "warning",
      high: "destructive",
    };
    return priorityMap[priority] || "secondary";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => {
          const task = row.original;
          return (
            <div>
              <div className="font-medium">{task.title}</div>
              {task.is_recurring && (
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Repeat className="h-3 w-3" />
                  Recurring ({task.recurrence_type})
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.description || "-"}</span>
        ),
      },
      {
        accessorKey: "priority",
        header: "Priority",
        cell: ({ row }) => (
          <Badge
            variant={getPriorityBadgeVariant(row.original.priority)}
            className="capitalize"
          >
            {row.original.priority}
          </Badge>
        ),
      },
      {
        accessorKey: "recurrence",
        header: "Recurrence",
        cell: ({ row }) => {
          const task = row.original;
          return task.is_recurring ? (
            <div className="flex items-center gap-1 text-sm text-green-600">
              <Repeat className="h-4 w-4" />
              <span>
                {task.recurrence_type} ({task.recurrence_value})
              </span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">One-time</span>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-sm">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleView(row.original)}>
                <Eye className="mr-2 h-4 w-4" /> View Details
              </DropdownMenuItem>
              {hasPermission("task update") && (
                <DropdownMenuItem onClick={() => openAddModal(row.original)}>
                  <Edit className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {hasPermission("task delete") && (
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => handleDelete(row.original.id)}
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],

    [hasPermission, openAddModal, handleDelete],
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Tasks Management"
          subtitle="Create and Manage Tasks"
        />
        <PageHeaderActions>
          {hasPermission("task assignment read") && (
            <Button
              variant="outline"
              onClick={() => setShowAssignmentsModal(true)}
            >
              <Users className="mr-2 h-4 w-4" />
              Task Assignments
            </Button>
          )}
          {hasPermission("task create") && (
            <Button onClick={() => openAddModal()}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add New Task
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search tasks..."
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={tasks}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          pageCount={paginationMeta.last_page}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            }
          }}
          isLoading={isLoading}
          enableRowSelection={true}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Tasks: {paginationMeta.total || 0}
            </span>
          }
        />
      </TableCard>

      {/* Add/Edit Task Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle>
              {isEdit ? "Edit Task" : "Create New Task"}
            </DialogTitle>
          </DialogHeader>

          <div className="px-5">
            <Tabs defaultValue="task" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="task">Task Details</TabsTrigger>
                {hasPermission("task assignment create") && (
                  <TabsTrigger value="assignment">Assignment</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="task" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>
                    Task Title <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={taskData.title}
                    onChange={(e) =>
                      setTaskData({ ...taskData, title: e.target.value })
                    }
                    maxLength={255}
                    placeholder="Enter task title"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={taskData.description}
                    onChange={(e) =>
                      setTaskData({ ...taskData, description: e.target.value })
                    }
                    placeholder="Enter task description"
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={taskData.priority}
                    onValueChange={(value) =>
                      setTaskData({ ...taskData, priority: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="recurring"
                    checked={taskData.is_recurring}
                    onCheckedChange={(checked) =>
                      setTaskData({ ...taskData, is_recurring: checked })
                    }
                  />
                  <Label htmlFor="recurring">Recurring Task</Label>
                </div>

                {taskData.is_recurring && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Recurrence Type</Label>
                      <Select
                        value={taskData.recurrence_type || ""}
                        onValueChange={(value) =>
                          setTaskData({ ...taskData, recurrence_type: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Recurrence Value</Label>
                      <Input
                        type="number"
                        min="1"
                        value={taskData.recurrence_value || ""}
                        onChange={(e) =>
                          setTaskData({
                            ...taskData,
                            recurrence_value: parseInt(e.target.value),
                          })
                        }
                        placeholder="Enter value"
                      />
                    </div>
                  </div>
                )}
              </TabsContent>

              {hasPermission("task assignment create") && (
                <TabsContent value="assignment" className="space-y-4 mt-4">
                  <p className="text-sm text-muted-foreground">
                    Optional: Assign this task to an employee
                  </p>

                  <div className="space-y-2">
                    <Label>Employee</Label>
                    <Select
                      value={assignmentData.employee_id}
                      onValueChange={(value) =>
                        setAssignmentData({
                          ...assignmentData,
                          employee_id: value,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id.toString()}>
                            {emp.full_name || emp.first_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Notifier</Label>
                    <Select
                      value={assignmentData.notifier_id}
                      onValueChange={(value) =>
                        setAssignmentData({
                          ...assignmentData,
                          notifier_id: value,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select notifier" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id.toString()}>
                            {user.username}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="datetime-local"
                        value={assignmentData.start_date}
                        onChange={(e) =>
                          setAssignmentData({
                            ...assignmentData,
                            start_date: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="datetime-local"
                        value={assignmentData.end_date}
                        onChange={(e) =>
                          setAssignmentData({
                            ...assignmentData,
                            end_date: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeAddModal}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveTaskMutation.isPending}>
              {saveTaskMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEdit ? "Update" : "Create"} Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Task Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Task Details</DialogTitle>
          </DialogHeader>

          {selectedTask && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Task Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Title:</span>
                    <span className="font-medium">{selectedTask.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Priority:</span>
                    <Badge
                      variant={getPriorityBadgeVariant(selectedTask.priority)}
                      className="capitalize"
                    >
                      {selectedTask.priority}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type:</span>
                    <span>
                      {selectedTask.is_recurring ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <Repeat className="h-4 w-4" />
                          Recurring ({selectedTask.recurrence_type})
                        </span>
                      ) : (
                        "One-time"
                      )}
                    </span>
                  </div>
                  {selectedTask.description && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground block mb-1">
                        Description:
                      </span>
                      <p className="text-sm">{selectedTask.description}</p>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-muted-foreground">Created:</span>
                    <span>
                      {new Date(selectedTask.created_at).toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Assignments Modal */}
      <Dialog
        open={showAssignmentsModal}
        onOpenChange={setShowAssignmentsModal}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Task Assignments
            </DialogTitle>
          </DialogHeader>

          <div className="px-5 pb-5">
            <div className="rounded-md border">
              <ShadcnTable>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Notifier</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[70px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignmentsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : assignments.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-muted-foreground"
                      >
                        No task assignments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignments.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell className="font-medium">
                          {assignment.task?.title || "-"}
                        </TableCell>
                        <TableCell>
                          {assignment.employee?.full_name || "-"}
                        </TableCell>
                        <TableCell>
                          {assignment.notifier?.username || "-"}
                        </TableCell>
                        <TableCell>
                          {formatDate(assignment.start_date)}
                        </TableCell>
                        <TableCell>
                          {assignment.end_date
                            ? formatDate(assignment.end_date)
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              assignment.status === "completed"
                                ? "success"
                                : assignment.status === "in_progress"
                                  ? "warning"
                                  : "secondary"
                            }
                            className="capitalize"
                          >
                            {assignment.status?.replace(/_/g, " ") || "Pending"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {hasPermission("task assignment delete") && (
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() =>
                                    handleDeleteAssignment(assignment.id)
                                  }
                                >
                                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </ShadcnTable>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the task and all its assignments.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isDeleteAssignmentDialogOpen}
        onOpenChange={setIsDeleteAssignmentDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to delete this assignment?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteAssignment}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default Tasks;
